/**
 * Shadow System Configuration
 * Defines elevation levels through shadows
 * Based on Material Design elevation system
 */

import { Shadows } from '@mui/material/styles';

/**
 * Custom shadow definitions
 * 5 elevation levels plus none (0)
 */
const shadowDefinitions = {
  /**
   * Level 0: No elevation
   * Flat surface, no shadow
   */
  none: 'none',

  /**
   * Level 1: Subtle elevation
   * Used for: Cards at rest, app bars
   * Slight shadow for minimal separation
   */
  sm: '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.08)',

  /**
   * Level 2: Low elevation
   * Used for: Raised buttons, hover cards, chips
   * Noticeable but not prominent
   */
  md: '0px 2px 6px rgba(0, 0, 0, 0.12), 0px 1px 4px rgba(0, 0, 0, 0.08)',

  /**
   * Level 3: Medium elevation
   * Used for: Dropdowns, menus, tooltips
   * Clear separation from background
   */
  lg: '0px 4px 12px rgba(0, 0, 0, 0.15), 0px 2px 6px rgba(0, 0, 0, 0.1)',

  /**
   * Level 4: High elevation
   * Used for: Modals, dialogs, popups
   * Strong shadow for important overlays
   */
  xl: '0px 8px 24px rgba(0, 0, 0, 0.18), 0px 4px 12px rgba(0, 0, 0, 0.12)',

  /**
   * Level 5: Maximum elevation
   * Used for: Full-screen modals, important notifications
   * Maximum shadow for critical overlays
   */
  '2xl': '0px 12px 32px rgba(0, 0, 0, 0.2), 0px 6px 16px rgba(0, 0, 0, 0.15)',
};

/**
 * Material-UI requires exactly 25 shadow definitions
 * We map our 6 levels (0-5) across the full 25 levels
 */
export const shadows: Shadows = [
  'none', // 0
  shadowDefinitions.sm, // 1
  shadowDefinitions.sm, // 2
  shadowDefinitions.sm, // 3
  shadowDefinitions.md, // 4
  shadowDefinitions.md, // 5
  shadowDefinitions.md, // 6
  shadowDefinitions.md, // 7
  shadowDefinitions.lg, // 8
  shadowDefinitions.lg, // 9
  shadowDefinitions.lg, // 10
  shadowDefinitions.lg, // 11
  shadowDefinitions.xl, // 12
  shadowDefinitions.xl, // 13
  shadowDefinitions.xl, // 14
  shadowDefinitions.xl, // 15
  shadowDefinitions.xl, // 16
  shadowDefinitions['2xl'], // 17
  shadowDefinitions['2xl'], // 18
  shadowDefinitions['2xl'], // 19
  shadowDefinitions['2xl'], // 20
  shadowDefinitions['2xl'], // 21
  shadowDefinitions['2xl'], // 22
  shadowDefinitions['2xl'], // 23
  shadowDefinitions['2xl'], // 24
];

/**
 * Elevation levels for semantic use
 * Use these constants for consistent elevation
 */
export const elevation = {
  none: 0,
  card: 1,
  cardHover: 4,
  button: 2,
  buttonHover: 4,
  appBar: 2,
  drawer: 8,
  dropdown: 8,
  menu: 8,
  tooltip: 8,
  modal: 16,
  dialog: 24,
  snackbar: 8,
  fab: 6,
  fabHover: 12,
};

/**
 * Custom shadow utilities
 * For use outside of MUI components
 */
export const customShadows = {
  // Inset shadows
  inset: {
    sm: 'inset 0px 1px 2px rgba(0, 0, 0, 0.08)',
    md: 'inset 0px 2px 4px rgba(0, 0, 0, 0.12)',
    lg: 'inset 0px 4px 8px rgba(0, 0, 0, 0.15)',
  },

  // Focus rings
  focus: {
    primary: '0 0 0 3px rgba(176, 141, 87, 0.22)',
    error: '0 0 0 3px rgba(182, 107, 91, 0.25)',
    success: '0 0 0 3px rgba(94, 140, 106, 0.25)',
  },

  // Special effects
  glow: {
    primary: '0 0 16px rgba(176, 141, 87, 0.18)',
    success: '0 0 16px rgba(94, 140, 106, 0.22)',
    warning: '0 0 16px rgba(176, 141, 87, 0.18)',
    error: '0 0 16px rgba(182, 107, 91, 0.22)',
  },
};
